/**
 * CommunicatieDetailDialog — Enterprise Communicatiecentrum detail-view.
 *
 * Sprint Fase A Afronding (22 Feb 2026) — herbouwd als moderne tab-interface.
 *
 * Tabs:
 *   1. Overzicht        — snel samengevat (afzender, ontvanger, onderwerp, verzonden, status)
 *   2. HTML Preview     — daadwerkelijke rendering van de e-mail body (sandbox iframe)
 *   3. Betrokkenen      — Aan/CC/BCC + gekoppelde klant
 *   4. Factuur          — gekoppelde factuur met status & bedrag
 *   5. Delivery         — Resend event-timeline (delivered/opened/clicked/bounced/complained)
 *   6. Stripe           — betaallink + payment intent + amount
 *   7. Bijlagen         — filename + size + mime
 *   8. Metadata         — trigger, template, language, source-info
 *   9. Event History    — chronologisch alle events (webhook + pixel)
 *
 * Extra acties (footer):
 *   - Opnieuw verzenden  (opent Composer)
 *   - Open als concept   (Composer in draft-modus)
 *   - Opslaan als template
 *   - Download EML
 *   - Download PDF
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, X, Mail, Send, CreditCard, ExternalLink, Copy,
  User, FileText, Zap, Info, Eye, MousePointerClick, XCircle,
  CheckCircle2, AlertTriangle, Clock, Paperclip, Download,
  FileDown, FilePlus2, Save, LayoutList,
} from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "long", timeStyle: "medium",
    });
  } catch { return iso; }
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

function fmtMoney(v, cur = "EUR") {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: (cur || "EUR").toUpperCase(),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${cur} ${Number(v).toFixed(2)}`; }
}

function copyToClipboard(txt, label = "Gekopieerd") {
  if (!txt) return;
  try {
    navigator.clipboard.writeText(String(txt));
    toast.success(label);
  } catch { /* silent */ }
}

// ─── Delivery event iconography ──────────────────────────────
const DELIVERY_EVENT_META = {
  sent:              { label: "Verzonden",      icon: Send,             tone: "info" },
  processed:         { label: "Verwerkt",       icon: CheckCircle2,     tone: "info" },
  delivered:         { label: "Afgeleverd",     icon: CheckCircle2,     tone: "success" },
  opened:            { label: "Geopend",        icon: Eye,              tone: "success" },
  clicked:           { label: "Geklikt",        icon: MousePointerClick, tone: "success" },
  bounced:           { label: "Bounce",         icon: XCircle,          tone: "danger" },
  complained:        { label: "Spam-klacht",    icon: AlertTriangle,    tone: "danger" },
  delivery_delayed:  { label: "Vertraging",     icon: Clock,            tone: "warning" },
  failed:            { label: "Mislukt",        icon: XCircle,          tone: "danger" },
  queued:            { label: "In wachtrij",    icon: Clock,            tone: "warning" },
  unsubscribed:      { label: "Uitgeschreven",  icon: XCircle,          tone: "warning" },
};

const TONE_BG = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  info:    "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger:  "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function CommunicatieDetailDialog({
  open, onOpenChange, source, messageId, onReopenComposer,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [templateSaveOpen, setTemplateSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  useEffect(() => {
    if (!open || !source || !messageId) return;
    setDetail(null);
    setActiveTab("overview");
    setLoading(true);
    (async () => {
      try {
        const r = await adminApi.get(
          `/admin/communications/messages/${source}/${messageId}`,
        );
        setDetail(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, source, messageId, onOpenChange]);

  const reopenAction = useCallback(async (asConcept = false) => {
    if (!detail) return;
    setReopening(true);
    try {
      const endpoint = asConcept
        ? `/admin/communications/messages/${detail.source}/${detail.raw?.id || messageId}/open-as-concept`
        : `/admin/communications/reminders/${detail.raw?.id || messageId}/reopen`;
      const r = await adminApi.post(endpoint);
      onReopenComposer?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReopening(false);
    }
  }, [detail, messageId, onReopenComposer, onOpenChange]);

  const downloadAsset = useCallback(async (ext) => {
    if (!detail) return;
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${backendBase}/api/admin/communications/messages/${detail.source}/${detail.raw?.id || messageId}/download.${ext}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download mislukt (${res.status})`);
      const blob = await res.blob();
      const contentDisp = res.headers.get("content-disposition") || "";
      const match = contentDisp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `bericht.${ext}`;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      toast.success(`Gedownload als ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message || `Download ${ext.toUpperCase()} mislukt`);
    }
  }, [detail, messageId, backendBase]);

  const saveAsTemplate = useCallback(async () => {
    if (!detail) return;
    setSavingTemplate(true);
    try {
      const r = await adminApi.post(
        `/admin/communications/messages/${detail.source}/${detail.raw?.id || messageId}/save-as-template`,
        { name: templateName },
      );
      toast.success(`Template opgeslagen: ${r.data.name}`);
      setTemplateSaveOpen(false);
      setTemplateName("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingTemplate(false);
    }
  }, [detail, messageId, templateName]);

  const canReopenComposer = detail && (detail.source === "reminder" || (detail.raw?.linked_objects?.invoice_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:max-w-4xl min-w-0 rounded-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden"
        data-testid="communicatie-detail-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-brand-900">
            <Mail className="w-5 h-5" />
            Bericht-details
          </DialogTitle>
          <DialogDescription className="sr-only">
            Bekijk het verzonden bericht, delivery-events, gekoppelde factuur en download opties.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12" data-testid="communicatie-detail-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : detail ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 min-w-0">
            <TabsList className="flex-shrink-0 mx-6 mt-3 flex-wrap gap-1 justify-start bg-slate-50 border border-slate-200 rounded-full p-1 h-auto">
              <TabsTrigger value="overview"    className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-overview">Overzicht</TabsTrigger>
              <TabsTrigger value="preview"     className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-preview">HTML Preview</TabsTrigger>
              <TabsTrigger value="parties"     className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-parties">Betrokkenen</TabsTrigger>
              <TabsTrigger value="invoice"     className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-invoice">Factuur</TabsTrigger>
              <TabsTrigger value="delivery"    className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-delivery">Delivery</TabsTrigger>
              <TabsTrigger value="stripe"      className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-stripe">Stripe</TabsTrigger>
              <TabsTrigger value="attachments" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-attachments">Bijlagen</TabsTrigger>
              <TabsTrigger value="metadata"    className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-metadata">Metadata</TabsTrigger>
              <TabsTrigger value="events"      className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-events">Event History</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto px-6 pb-4 pt-2">
              <TabsContent value="overview"    className="mt-2 space-y-3"><OverviewTab detail={detail} /></TabsContent>
              <TabsContent value="preview"     className="mt-2"><HtmlPreviewTab detail={detail} /></TabsContent>
              <TabsContent value="parties"     className="mt-2 space-y-3"><PartiesTab detail={detail} /></TabsContent>
              <TabsContent value="invoice"     className="mt-2 space-y-3"><InvoiceTab detail={detail} /></TabsContent>
              <TabsContent value="delivery"    className="mt-2 space-y-3"><DeliveryTab detail={detail} /></TabsContent>
              <TabsContent value="stripe"      className="mt-2 space-y-3"><StripeTab detail={detail} /></TabsContent>
              <TabsContent value="attachments" className="mt-2 space-y-3"><AttachmentsTab detail={detail} /></TabsContent>
              <TabsContent value="metadata"    className="mt-2 space-y-3"><MetadataTab detail={detail} /></TabsContent>
              <TabsContent value="events"      className="mt-2 space-y-3"><EventHistoryTab detail={detail} /></TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className="p-8 text-center text-slate-500">Bericht niet gevonden</div>
        )}

        <DialogFooter className="px-6 py-3 border-t border-slate-200 gap-2 flex-wrap sm:flex-nowrap flex-shrink-0">
          <div className="flex flex-wrap items-center gap-2 mr-auto">
            <Button
              variant="outline" size="sm"
              onClick={() => downloadAsset("eml")}
              className="rounded-full text-xs"
              data-testid="detail-download-eml"
              disabled={!detail}
            >
              <FileDown className="w-3.5 h-3.5 mr-1" />
              EML
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => downloadAsset("pdf")}
              className="rounded-full text-xs"
              data-testid="detail-download-pdf"
              disabled={!detail}
            >
              <FileDown className="w-3.5 h-3.5 mr-1" />
              PDF
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setTemplateSaveOpen(true)}
              className="rounded-full text-xs"
              data-testid="detail-save-template"
              disabled={!detail}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              Opslaan als template
            </Button>
            {canReopenComposer && (
              <Button
                variant="outline" size="sm"
                onClick={() => reopenAction(true)}
                disabled={reopening}
                className="rounded-full text-xs"
                data-testid="detail-open-concept"
              >
                <FilePlus2 className="w-3.5 h-3.5 mr-1" />
                Open als concept
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="detail-close"
          >
            Sluiten
          </Button>
          {canReopenComposer && (
            <Button
              onClick={() => reopenAction(false)}
              disabled={reopening}
              className="rounded-full bg-brand-500 hover:bg-brand-500/90 text-white"
              data-testid="communicatie-reopen-composer"
            >
              {reopening
                ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <Send className="w-3.5 h-3.5 mr-1" />}
              Opnieuw verzenden
            </Button>
          )}
        </DialogFooter>

        {/* Template save inline dialog */}
        {templateSaveOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setTemplateSaveOpen(false)}
            data-testid="template-save-overlay"
          >
            <div
              className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-3"
              onClick={(e) => e.stopPropagation()}
              data-testid="template-save-dialog"
            >
              <h3 className="font-semibold text-brand-900">Template opslaan</h3>
              <Label htmlFor="tpl-name" className="text-xs text-slate-600">Naam</Label>
              <Input
                id="tpl-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="bv. Herinnering (aangepast)"
                data-testid="template-save-name-input"
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setTemplateSaveOpen(false)}
                        className="rounded-full">
                  Annuleren
                </Button>
                <Button size="sm" onClick={saveAsTemplate} disabled={savingTemplate}
                        className="rounded-full bg-brand-500 hover:bg-brand-500/90 text-white"
                        data-testid="template-save-confirm">
                  {savingTemplate ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Opslaan
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Tab components ─────────────────────────────────────────── */

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="min-w-0 border border-slate-200 rounded-xl p-3 bg-white" data-testid={testid}>
      <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </h4>
      {children}
    </section>
  );
}

function Row({ label, value, mono = false, copyable = false, testid }) {
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-start gap-3 py-1 text-sm" data-testid={testid}>
      <div className="w-full sm:w-40 flex-shrink-0 text-slate-500">{label}</div>
      <div className={`flex-1 min-w-0 ${mono ? "font-mono text-[12px]" : ""} text-brand-900 break-words`}>
        {value == null || value === "" ? <em className="text-slate-400">—</em> : value}
      </div>
      {copyable && value && (
        <button type="button" onClick={() => copyToClipboard(String(value))}
                className="text-slate-400 hover:text-brand-500" title="Kopieer">
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function OverviewTab({ detail }) {
  const m = detail.message || {};
  const raw = detail.raw || {};
  const typeMap = {
    reminder: { label: "Herinnering", color: "bg-amber-100 text-amber-800 border-amber-200" },
    email:    { label: "E-mail",      color: "bg-blue-100 text-blue-800 border-blue-200" },
    stripe:   { label: "Stripe",      color: "bg-brand-100 text-brand-900 border-brand-200" },
  };
  const t = typeMap[m.type] || typeMap.email;
  const primaryStatus = (raw.status || m.status || "sent").toLowerCase();
  const statusMeta = DELIVERY_EVENT_META[primaryStatus] || DELIVERY_EVENT_META.sent;
  const StatusIcon = statusMeta.icon;
  const bgClass = TONE_BG[statusMeta.tone] || TONE_BG.neutral;

  return (
    <>
      <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${t.color} rounded-full text-[10px] border`}>{t.label}</Badge>
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${bgClass}`}>
            <StatusIcon className="w-3 h-3" />
            {statusMeta.label}
          </div>
          <span className="ml-auto text-xs text-slate-500">{fmtDate(m.occurred_at)}</span>
        </div>
        <div className="mt-3 font-bold text-brand-900 text-lg">
          {m.subject || m.stripe_event_type || m.email_type || "(zonder onderwerp)"}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {m.customer_name && <span>{m.customer_name} · </span>}
          {m.customer_email || raw.recipient || "—"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="border border-slate-200 rounded-xl p-3 bg-white">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Delivery</div>
          <div className="flex items-center gap-1.5">
            <StatusIcon className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold text-brand-900">{statusMeta.label}</span>
          </div>
          {raw.open_count > 0 && (
            <div className="mt-1 text-xs text-slate-500">
              Geopend {raw.open_count}× · Geklikt {raw.click_count || 0}×
            </div>
          )}
        </div>
        <div className="border border-slate-200 rounded-xl p-3 bg-white">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Resend Message ID</div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[11px] text-brand-900 truncate">
              {raw.provider_message_id || raw.provider_id || "—"}
            </span>
            {(raw.provider_message_id || raw.provider_id) && (
              <button type="button"
                      onClick={() => copyToClipboard(raw.provider_message_id || raw.provider_id, "Message ID gekopieerd")}
                      className="text-slate-400 hover:text-brand-500" title="Kopieer">
                <Copy className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function HtmlPreviewTab({ detail }) {
  const raw = detail.raw || {};
  const html = raw.html_body;
  const plain = raw.text_body;
  const [mode, setMode] = useState(html ? "html" : "text");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {html && (
          <button
            type="button"
            onClick={() => setMode("html")}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              mode === "html"
                ? "bg-brand-900 text-white border-brand-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-brand-500"
            }`}
            data-testid="preview-mode-html"
          >
            HTML
          </button>
        )}
        {plain && (
          <button
            type="button"
            onClick={() => setMode("text")}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              mode === "text"
                ? "bg-brand-900 text-white border-brand-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-brand-500"
            }`}
            data-testid="preview-mode-text"
          >
            Tekst
          </button>
        )}
      </div>

      {mode === "html" && html && (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <iframe
            srcDoc={html}
            title="E-mail preview"
            sandbox=""
            className="w-full min-h-[420px]"
            style={{ height: "60vh" }}
            data-testid="html-preview-iframe"
          />
        </div>
      )}

      {mode === "text" && plain && (
        <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-[13px] whitespace-pre-wrap font-mono text-slate-800 max-h-[60vh] overflow-auto"
             data-testid="text-preview">
          {plain}
        </pre>
      )}

      {!html && !plain && (
        <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500 bg-slate-50">
          <FileText className="w-6 h-6 mx-auto mb-2 text-slate-300" />
          Geen e-mail-inhoud beschikbaar voor dit bericht. Voor herinneringen wordt de body dynamisch gegenereerd bij verzending.
        </div>
      )}
    </div>
  );
}

function PartiesTab({ detail }) {
  const m = detail.message || {};
  const raw = detail.raw || {};
  return (
    <Section title="Betrokkenen" icon={User}>
      <Row label="Datum & tijd" value={fmtDate(m.occurred_at)} testid="detail-datetime" />
      <Row label="Afzender (systeem)" value={m.sent_by || raw.from_email || raw.sent_by_email || "—"} testid="detail-sender" />
      <Row label="Ontvanger (Aan)" value={m.customer_email || (Array.isArray(raw.to) ? raw.to.join(", ") : raw.to) || raw.recipient || "—"} testid="detail-to" />
      {(raw.cc || []).length > 0 && (<Row label="CC" value={(raw.cc || []).join(", ")} testid="detail-cc" />)}
      {(raw.bcc || []).length > 0 && (<Row label="BCC" value={(raw.bcc || []).join(", ")} testid="detail-bcc" />)}
      <Row label="Onderwerp" value={m.subject || raw.subject || "—"} testid="detail-subject" />
      {m.customer_name && (<Row label="Klantnaam" value={m.customer_name} />)}
    </Section>
  );
}

function InvoiceTab({ detail }) {
  const inv = detail.invoice;
  const raw = detail.raw || {};
  if (!inv && !raw.invoice_id) {
    return (
      <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500 bg-slate-50">
        <FileText className="w-6 h-6 mx-auto mb-2 text-slate-300" />
        Dit bericht is niet gekoppeld aan een factuur.
      </div>
    );
  }
  return (
    <Section title="Gekoppelde factuur" icon={FileText} testid="detail-invoice-block">
      <Row label="Factuurnr" value={inv?.invoice_number || raw.invoice_id} mono />
      {inv && (
        <>
          <Row label="Klant" value={inv.customer_name || "—"} />
          <Row label="Klant-e-mail" value={inv.customer_email || "—"} />
          <Row label="Financiële status" value={inv.financial_status || "—"} />
          <Row label="Totaal (incl. BTW)" value={fmtMoney(inv.total_incl_vat, inv.currency)} />
          <Row label="Betaald" value={fmtMoney(inv.total_paid, inv.currency)} />
          <Row label="Openstaand" value={fmtMoney(inv.amount_due, inv.currency)} />
        </>
      )}
      <Row label="Snapshot bedrag (bij verzending)"
           value={raw.amount_due_snapshot != null ? fmtMoney(raw.amount_due_snapshot) : "—"} />
    </Section>
  );
}

function DeliveryStatusIndicator({ raw, m }) {
  const st = (raw.status || m.status || "sent").toLowerCase();
  const meta = DELIVERY_EVENT_META[st] || DELIVERY_EVENT_META.sent;
  const Ic = meta.icon;
  return (
    <div className="flex items-center gap-1.5">
      <Ic className="w-4 h-4" />
      <span>{meta.label}</span>
    </div>
  );
}

function DeliveryTab({ detail }) {
  const raw = detail.raw || {};
  const m = detail.message || {};

  const events = raw.events || [];
  const orderedEvents = [...events].sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <>
      <Section title="Huidige status" icon={Zap}>
        <Row label="Delivery status" value={<DeliveryStatusIndicator raw={raw} m={m} />} />
        <Row label="Resend Message ID"
             value={raw.provider_message_id || raw.provider_id || "—"} mono copyable
             testid="detail-resend-id" />
        <Row label="Afgeleverd op" value={fmtDate(raw.delivered_at)} />
        <Row label="Voor het eerst geopend" value={fmtDate(raw.first_opened_at)} />
        <Row label="Laatst geopend" value={fmtDate(raw.last_opened_at)} />
        <Row label="Aantal keer geopend" value={raw.open_count ?? 0} />
        <Row label="Aantal keer geklikt" value={raw.click_count ?? 0} />
        {raw.bounce_reason && <Row label="Bounce reden" value={raw.bounce_reason} />}
        {raw.error && <Row label="Foutmelding" value={<span className="text-red-700">{raw.error}</span>} />}
      </Section>

      {orderedEvents.length > 0 && (
        <Section title={`Event-timeline (${orderedEvents.length})`} icon={LayoutList}>
          <ul className="space-y-1.5">
            {orderedEvents.map((ev, i) => {
              const key = (ev.type || "").split(".").pop() || ev.type || "unknown";
              const meta = DELIVERY_EVENT_META[key] || { label: ev.type, icon: Info, tone: "neutral" };
              const Ic = meta.icon;
              const bg = TONE_BG[meta.tone] || TONE_BG.neutral;
              return (
                <li key={i} className={`flex items-center gap-2 text-sm px-2 py-1.5 border rounded-lg ${bg}`}
                    data-testid={`event-row-${i}`}>
                  <Ic className="w-4 h-4 flex-shrink-0" />
                  <span className="font-semibold">{meta.label}</span>
                  <span className="text-xs text-slate-500 ml-auto tabular-nums">{fmtDateShort(ev.at)}</span>
                  {ev.source && <span className="text-[10px] uppercase text-slate-400 font-mono">{ev.source}</span>}
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </>
  );
}

function StripeTab({ detail }) {
  const s = detail.stripe;
  const m = detail.message || {};
  if (!s?.payment_url && !m.payment_intent && !m.session_id && !m.amount) {
    return (
      <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500 bg-slate-50">
        <CreditCard className="w-6 h-6 mx-auto mb-2 text-slate-300" />
        Geen Stripe-informatie bij dit bericht.
      </div>
    );
  }
  return (
    <Section title="Stripe & betaling" icon={CreditCard} testid="detail-stripe-block">
      {(s?.payment_url || m.stripe_pay_url) && (
        <Row label="Betaallink" value={
          <a href={s?.payment_url || m.stripe_pay_url} target="_blank" rel="noreferrer"
             className="text-brand-500 hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Open bij Stripe
          </a>
        } />
      )}
      {(s?.payment_intent || m.payment_intent) && (
        <Row label="Payment Intent" value={s?.payment_intent || m.payment_intent} mono copyable />
      )}
      {m.session_id && (<Row label="Session ID" value={m.session_id} mono copyable />)}
      {m.amount != null && (<Row label="Bedrag" value={fmtMoney(m.amount, m.currency)} />)}
    </Section>
  );
}

function AttachmentsTab({ detail }) {
  const raw = detail.raw || {};
  const attachments = raw.attachments || [];
  const documents = raw.linked_objects?.documents || [];
  if (attachments.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500 bg-slate-50">
        <Paperclip className="w-6 h-6 mx-auto mb-2 text-slate-300" />
        Geen bijlagen bij dit bericht.
      </div>
    );
  }
  return (
    <Section title={`Bijlagen (${attachments.length})`} icon={Paperclip} testid="detail-attachments-block">
      <ul className="space-y-1">
        {attachments.map((a, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2 min-w-0 text-sm py-1.5 px-2 border border-slate-100 rounded-lg hover:bg-slate-50"
              data-testid={`attachment-row-${i}`}>
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="flex-1 min-w-0 break-all font-medium text-brand-900" data-testid={`attachment-filename-${i}`}>{a.filename || `Bijlage ${i + 1}`}</span>
            {a.mime && <span className="text-[10px] text-slate-400 font-mono">{a.mime}</span>}
            {a.size && (
              <span className="text-xs text-slate-500 tabular-nums">
                {Math.round(a.size / 1024)} KB
              </span>
            )}
          </li>
        ))}
      </ul>
      {documents.map((doc, index) => <div key={`${doc.document_id}-${index}`} className="mt-3 min-w-0 border-t pt-2 text-xs text-slate-600" data-testid={`sent-document-${index}`}>
        <p className="break-all" data-testid={`sent-document-id-${index}`}>{doc.document_id}</p>
        <p className="break-all" data-testid={`sent-document-version-${index}`}>Versie {doc.version_number || 1}{doc.revision != null ? ` · revisie ${doc.revision}` : ""} · {doc.document_version_id}</p>
        {doc.version_date && <p data-testid={`sent-document-date-${index}`}>{fmtDate(doc.version_date)}</p>}
        {doc.sha256 && <p className="break-all font-mono" data-testid={`sent-document-sha256-${index}`}>SHA-256: {doc.sha256}</p>}
      </div>)}
    </Section>
  );
}

function MetadataTab({ detail }) {
  const raw = detail.raw || {};
  const trigger = raw.trigger;
  const sourceLabels = {
    daily_batch: "Automatische dagelijkse batch",
    single_manual: "Handmatig via 'Snel' knop",
    composer: "Handmatig via Reminder Composer",
    scheduler: "Scheduler",
  };
  return (
    <>
      <Section title="Extra info" icon={Info}>
        {trigger && <Row label="Trigger" value={sourceLabels[trigger] || trigger} />}
        {raw.language && <Row label="Taal" value={raw.language.toUpperCase()} />}
        {raw.reminder_type && <Row label="Reminder-type" value={raw.reminder_type} />}
        {raw.template_id && <Row label="Template ID" value={raw.template_id} mono />}
        {raw.channel && <Row label="Kanaal" value={raw.channel} />}
        {raw.direction && <Row label="Richting" value={raw.direction} />}
        {detail.source === "stripe" && raw.event_id && (
          <Row label="Stripe event-ID" value={raw.event_id} mono copyable />
        )}
      </Section>
      {(raw.ai_summary || raw.ai_intent || raw.ai_sentiment) && (
        <Section title="AI-signalen (Fase B)" icon={Zap}>
          {raw.ai_summary && <Row label="AI-samenvatting" value={raw.ai_summary} />}
          {raw.ai_intent && <Row label="AI-intent" value={raw.ai_intent} />}
          {raw.ai_sentiment && <Row label="AI-sentiment" value={raw.ai_sentiment} />}
        </Section>
      )}
    </>
  );
}

function EventHistoryTab({ detail }) {
  const raw = detail.raw || {};
  const events = raw.events || [];
  const linked = raw.linked_objects || {};
  return (
    <>
      <Section title={`Event-log (${events.length})`} icon={LayoutList}>
        {events.length === 0 ? (
          <div className="text-xs text-slate-500 italic">Nog geen webhook-events geregistreerd voor dit bericht.</div>
        ) : (
          <ul className="space-y-1.5">
            {events.map((ev, i) => (
              <li key={i} className="text-sm border-l-2 border-brand-500 pl-3 py-1"
                  data-testid={`event-history-row-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-brand-900">{ev.type}</span>
                  <span className="text-xs text-slate-500 ml-auto tabular-nums">{fmtDateShort(ev.at)}</span>
                </div>
                <div className="flex gap-3 mt-0.5 text-xs text-slate-500">
                  {ev.source && <span>bron: {ev.source}</span>}
                  {ev.provider_event_id && <span className="font-mono">evt: {ev.provider_event_id.slice(0, 12)}…</span>}
                  {ev.user_agent && <span title={ev.user_agent}>UA: {ev.user_agent.slice(0, 30)}…</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {Object.keys(linked).length > 0 && (
        <Section title="Gekoppelde entiteiten" icon={FileText}>
          {Object.entries(linked).map(([k, v]) => (
            v ? <Row key={k} label={k} value={String(v)} mono copyable /> : null
          ))}
        </Section>
      )}
    </>
  );
}
